import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type { ConnectedAppCreateSchema, ConnectedAppMutationSchema } from './schemas';
import type { APIKeyFormSchema, APIKeyMutationSchema } from './schemas/api-keys-schemas';
import type { ConnectedAppQueryAllSchema, ConnectedAppQuerySchema } from './schemas/connected-app-query-schema';

export function getConnectedAppsQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['CONNECTED_APPS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<ConnectedAppQueryAllSchema>({
        endpoint: '/api/v1/api-integrations?include=brand.icon,legal_entity.logo',
        params: `${apiParamStr}`,
      });

      return res;
    },
  };
}

export function useGetConnectedApps(apiParamStr: string) {
  return useSuspenseQuery(getConnectedAppsQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['CONNECTED_APPS'] });
};

export function useGetConnectedApp(id: string) {
  return useSuspenseQuery(getConnectedAppQuery(id));
}

export function getConnectedAppQuery(id: string) {
  return {
    queryKey: ['CONNECTED_APP', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ConnectedAppQuerySchema }>({
        endpoint: `/api/v1/api-integrations/${id}`,
        params: `?include=brand.icon,legal_entity.logo,api_keys.last_updated_by.profile_images,api_keys.created_by.profile_images`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateConnectedAppQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['CONNECTED_APP', id] });
};

export const useGetConnectedAppMutation: UseControlledMutation<ConnectedAppQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['CONNECTED_APP_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<ConnectedAppQueryAllSchema>({
            endpoint: '/api/v1/api-integrations',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: ConnectedAppQuerySchema }, unknown, ConnectedAppCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/api-integrations/',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: ConnectedAppQuerySchema }, ApiError, ConnectedAppMutationSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/api-integrations/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'api-integrations', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: ConnectedAppQuerySchema }, ApiError, Partial<ConnectedAppMutationSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/api-integrations/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['CONNECTED_APP_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/api-integrations/${id}`,
        method: 'DELETE',
      }),
  });

export const useApiKeysBulkCreateMutation = () =>
  useMutation<APIKeyFormSchema[], ApiError, { items: APIKeyMutationSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/api-keys/bulk-store',
        payload,
      }),
  });

export const useApiKeysBulkUpdateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, { items: APIKeyMutationSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/api-keys/bulk-update',
        payload,
      }),
  });

export const useApiKeysBulkDeleteMutation = () =>
  useMutation<
    { data: { id: number } },
    ApiError,
    {
      items: number[];
    }
  >({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/api-keys/bulk-delete',
        payload,
      }),
  });

export const useDisconnectMutation = (id: string) =>
  useMutation<{ message: string }, ApiError>({
    mutationFn: () =>
      apiClient.request({
        endpoint: `/api/v1/api-integrations/${id}/disconnect`,
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'api-integrations', id] });
      invalidateConnectedAppQuery(id);
    },
  });
